// Promo code typed config (ADR-023 §3).
// Copied into each consumer — no npm workspaces at MVP.
// Phase 6: replace with Stripe Coupons API lookup.

export type PromoCode = {
  code: string;
  discount: number;
  duration: '3mo' | '12mo' | 'forever';
  appliesTo: ('T1' | 'T2' | 'T3')[];
  validUntil?: string;
  stripeCouponId?: string;
};

export const PROMO_CODES: Record<string, PromoCode> = {
  '3M50': {
    code: '3M50',
    discount: 0.5,
    duration: '3mo',
    appliesTo: ['T1', 'T2'],
  },
  '12M50': {
    code: '12M50',
    discount: 0.5,
    duration: '12mo',
    appliesTo: ['T1', 'T2'],
  },
  BETA50: {
    code: 'BETA50',
    discount: 0.5,
    duration: '3mo',
    appliesTo: ['T1', 'T2'],
  },
};

export function lookupPromo(code: string): PromoCode | null {
  return PROMO_CODES[code] ?? null;
}

export function isPromoValid(promo: PromoCode, tier: 'T1' | 'T2' | 'T3'): boolean {
  if (!promo.appliesTo.includes(tier)) return false;
  if (promo.validUntil && new Date(promo.validUntil) < new Date()) return false;
  return true;
}

// ── Pricing model (Early Adopter / rack, commitment-based) ───────────────
//
// ⛔ NO PRICE LIVES IN THIS FILE (ADR-085; ADR-073 Am.5 §A5.6, 5 October 2026). Every number a
// customer reads — the plan card, "Billed … once", the badge, the order summary — is computed from
// the zone's live catalogue (`GET /portal/catalogue` → `getCataloguePricing`). The static per-tier
// table that used to sit here was the "fallback" when the catalogue had not loaded, which meant the
// portal could show a price the catalogue did not hold — e.g. a flat 50% Early Adopter price on the
// 3-month term after Jeff had set it to 25%. A step whose catalogue has not loaded now says so
// rather than guessing. `portal-subscriber/test/no-literal-prices.test.ts` keeps it that way.
//
// STAFF FORK (7 October 2026): this section is now the subscriber copy's, verbatim. The staff fork
// had kept the static table (50% off both committed terms, rounded down), so the staff-mounted plan
// step quoted $99/mo on the 3-month term the catalogue bills at 25% off. Both forks now price from
// the catalogue; `portal-staff/test/no-literal-prices.test.ts` and `obw-pricing-parity.test.ts`
// keep them that way.
//
// The commitment mechanic (Jeff, 10 June 2026, refined 5 October 2026): RACK on Monthly (no
// commitment); the Early Adopter overlay on the committed terms, deeper for a longer commitment
// (catalogue `valueByTerm`), for the overlay's duration, then reverting to RACK at renewal.
//
// MVP shows TWO tiers (NexaGrowth, NexaSuccess). NexaContext (T3) is post-MVP
// and excluded from the subscriber Subscribe step.
//
// DISPLAY ONLY at MVP — real Stripe processing is LAUNCH (5d-4).

export type Tier = 'T1' | 'T2' | 'T3';

export const TIER_NAMES: Record<Tier, string> = {
  T1: 'NexaGrowth',
  T2: 'NexaSuccess',
  T3: 'NexaContext',
};

/** Tiers offered to subscribers at MVP. NexaContext (T3) is post-MVP. */
export const MVP_SUBSCRIBER_TIERS: Tier[] = ['T1', 'T2'];

// ── Commitment terms ─────────────────────────────────────────────────────
export type CommitmentTerm = 'monthly' | 'quarterly' | 'annual';

export const COMMITMENT_MONTHS: Record<CommitmentTerm, number> = {
  monthly: 1,
  quarterly: 3,
  annual: 12,
};

export const COMMITMENT_LABELS: Record<CommitmentTerm, string> = {
  monthly: 'Monthly',
  quarterly: '3 months',
  annual: '12 months',
};

export interface CommitmentPricing {
  term: CommitmentTerm;
  months: number;
  /** Effective per-month rate for this term (EA when committed, else rack). */
  monthlyRate: number;
  /** Rack per-month for the tier (renewal rate). */
  rackMonthly: number;
  isEa: boolean;
  /** Amount billed now for the committed term (monthly = one month). */
  upfrontTotal: number;
  /** What the same term would cost at rack — for the savings framing. */
  rackTermTotal: number;
  /** rackTermTotal − upfrontTotal. */
  savingsVsRack: number;
  /** savingsVsRack expressed as whole months of rack (for "~N months free"). */
  monthsFreeEquivalent: number;
}

/**
 * Compute the commitment pricing for a tier + term from the LIVE catalogue
 * (ADR-085 — no hardcoded prices: displayed tier prices come from
 * GET /portal/catalogue). Returns null when the catalogue has no priced row for
 * this tier + term — the caller then shows no price for it, never a guessed one.
 */
export function getCataloguePricing(
  cat: import('./catalogue').Catalogue,
  tier: Tier,
  term: CommitmentTerm,
): CommitmentPricing | null {
  const product = cat.products.find((p) => p.tierCode === tier);
  const row = product?.priced ? product.terms[term] : undefined;
  if (!row) return null;
  const months = COMMITMENT_MONTHS[term];
  const rackMonthly = row.monthlyEquivalent;
  const isEa = !!row.ea;
  const monthlyRate = row.ea ? row.ea.monthlyEquivalent : rackMonthly;
  const upfrontTotal = row.ea ? row.ea.sub : row.sub;
  const rackTermTotal = row.sub;
  const savingsVsRack = rackTermTotal - upfrontTotal;
  return {
    term,
    months,
    monthlyRate,
    rackMonthly,
    isEa,
    upfrontTotal,
    rackTermTotal,
    savingsVsRack,
    monthsFreeEquivalent: rackMonthly > 0 ? Math.round(savingsVsRack / rackMonthly) : 0,
  };
}

/**
 * Catalogue prices can be fractional (e.g. EA $99.50) — show cents only when present so whole-dollar
 * prices render unchanged.
 */
export function fmtUsd(n: number): string {
  const rounded = Math.round((n + Number.EPSILON) * 100) / 100;
  // Thousands grouped ("1,194"), as Billing's `money()` already shows the same figure — one sign-up
  // must not read "$1194" on the plan step and "$1,194" on the invoice.
  return rounded.toLocaleString('en-US', Number.isInteger(rounded)
    ? { maximumFractionDigits: 0 }
    : { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * The ONE displayed price for a tier + term, shared by the plan step and the payment step.
 *
 * ⭐ WHY ONE FUNCTION (5 October 2026, card-first UAT capture). The two steps each computed their own:
 * the plan step from the live catalogue, unrounded ($99.50/mo, billed $1194), and the payment step
 * from the static fallback table, rounded to whole dollars ($99/mo, billed $1188), so one sign-up
 * showed two different prices for the same plan. PL24: a promo never stacks on the Early Adopter
 * overlay; take the single best discount off rack (the server resolves exactly one overlay).
 */
export function displayPrice(pricing: CommitmentPricing, promoDiscount: number) {
  const eaImpliedDiscount =
    pricing.isEa && pricing.rackMonthly > 0 ? 1 - pricing.monthlyRate / pricing.rackMonthly : 0;
  const effectiveDiscount = Math.max(eaImpliedDiscount, promoDiscount);
  const effectiveMonthly = pricing.rackMonthly * (1 - effectiveDiscount);
  return {
    effectiveMonthly,
    termTotal: effectiveMonthly * pricing.months,
    showStrike: effectiveDiscount > 0,
  };
}

/** The whole-percent discount a term's price carries off rack (0 on Monthly / an unpriced term). */
export function termDiscountPercent(pricing: CommitmentPricing | null): number {
  if (!pricing || pricing.rackMonthly <= 0) return 0;
  return Math.round((1 - pricing.monthlyRate / pricing.rackMonthly) * 100);
}

/**
 * Which term is the BEST VALUE for a tier, and what it saves — DERIVED from the catalogue's numbers,
 * never pinned to a term (ADR-073 Am.5 §A5.6: "re-derive the BEST VALUE badge from the numbers").
 * Best value = the lowest monthly-equivalent price, and only when it is strictly below rack (a
 * badge on a term that saves nothing would be a lie). `monthsFree` is what that term saves over
 * paying rack for the same months, in whole months of rack. Null when no term saves anything.
 */
export function bestValueTerm(
  cat: import('./catalogue').Catalogue,
  tier: Tier,
  terms: CommitmentTerm[] = ['monthly', 'quarterly', 'annual'],
): { term: CommitmentTerm; monthsFree: number; percent: number } | null {
  let best: CommitmentPricing | null = null;
  for (const term of terms) {
    const p = getCataloguePricing(cat, tier, term);
    if (!p || p.monthlyRate >= p.rackMonthly) continue;
    if (!best || p.monthlyRate < best.monthlyRate) best = p;
  }
  if (!best) return null;
  return { term: best.term, monthsFree: best.monthsFreeEquivalent, percent: termDiscountPercent(best) };
}
